import type { Row } from '@tanstack/react-table';
import {
  ChevronRight,
  FolderPlus
} from 'lucide-react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useShellHeader } from '@/components/contexts/ShellHeaderContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { getTimezone } from '@/components/contexts/TimezoneContext';
import { canRowExpand, ExpandedAttachmentRows } from '@/components/correspondence/ExpandedAttachmentRows';
import { CreateFolderDialog, ScrollableFolderPath } from '@/components/folders';
import {
  FolderTableRow,
  buildFolderTableData,
  buildFolderContentCounts,
  buildFolderTreeIdSet,
  enhanceColumnsForFolderRows,
  getFolderIdsToFetch,
  getFolderTableHandlers,
  buildFolderTreeMap,
} from '@/components/folders/folder-table-utils';
import { FileUpload, IsolatedDataTable, PageLoading, TagEditorDialog } from '@/components/shared';
import type { FileUploadHandles } from '@/components/shared/FileUpload';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import { ConnectSourcesControls } from '@/components/shared/OutlookSyncProgress';
import { Button } from '@/components/ui/NewButton';
import { ColumnsCaseFiles } from '@/components/ui/columns-case-files';
import { ColumnsCorrespondence } from '@/components/ui/columns-correspondence';
import { deriveFilterFields, type FilterField, type FilterFieldOption } from '@/components/ui/data-table-filter-toolbar';
import { DEEP_ANALYSIS_LABEL } from '@/hooks/useAnalysisSet';
import { useBatchCompletionRefresh } from '@/hooks/useBatchCompletionRefresh';
import { useDeepAnalysisAvailability } from '@/hooks/useDeepAnalysisAvailability';
import { type Document, useDocuments } from '@/hooks/useDocuments';
import { useFolders, useFolderTree } from '@/hooks/useFolders';
import { useDeadlines } from '@/hooks/useDeadlines';
import { CORRESPONDENCE_ATTACHMENTS_FOLDER_PATH } from '@/constants/document-constants';
import { backendClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { buildDeleteQueryString, getDeleteAttachmentCount } from '@/lib/deleteOptions';
import { isClientDocumentsRoot } from '@/lib/eca-client-documents';
import { filterFilesWithToast } from '@/lib/fileFilterUtils';
import { toolbarActionLabelClass } from '@/lib/toolbar-compact';
import type { DocumentDeleteOptions } from '@/types/document';
import { isCorrespondenceFolder, isInCorrespondenceFolderTree } from '@/utils/document-utils';

const FolderView = () => {
  const { selectedCase } = useCaseContext();
  const { openDocumentPanel } = useSidePanel();
  const navigate = useNavigate();
  const { slug, folderGuid } = useParams({ from: '/matter/$slug/case-files/folder/$folderGuid' });
  const search = useSearch({ from: '/matter/$slug/case-files/folder/$folderGuid' });
  const timezone = getTimezone();
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null);
  const { isEnabled: deepAnalysisEnabled } = useDeepAnalysisAvailability(slug as string | undefined);
  const deepAnalysisOnly = deepAnalysisEnabled && search.view === 'deep-analysis';
  const setDeepAnalysisOnly = useCallback((nextValue: boolean) => {
    navigate({
      to: '/matter/$slug/case-files/folder/$folderGuid',
      params: { slug, folderGuid },
      search: nextValue ? { view: 'deep-analysis' } : {},
    });
  }, [navigate, slug, folderGuid]);

  // Get folder information
  const {
    folders,
    isLoading: foldersLoading,
    isValidating: foldersValidating,
    error: foldersError,
    mutate: mutateFoldersList,
  } = useFolders(slug as string);
  const currentFolder = folders.find(f => f.guid === folderGuid as string);

  // Fetch this folder's documents as table rows. Direct child folders appear as inline rows
  // (clicking one navigates into it). Child-folder documents are not fetched here — folder
  // row counts come from the folder tree instead.
  const folderIdsToFetch = useMemo(
    () => getFolderIdsToFetch(currentFolder?.folder_id),
    [currentFolder?.folder_id],
  );

  // Get documents in this folder with folder-based filtering. Without a resolved folder
  // there is no filter, and the request would fetch the whole matter.
  const { documents, error, mutate, isLoading: documentsLoading } = useDocuments(
    currentFolder ? slug as string : undefined,
    undefined, // documentTypes
    undefined, // isDraft
    folderIdsToFetch, // folderIds
    {},
    timezone,
    { analysisSetOnly: deepAnalysisOnly }
  );

  // Get folder tree for navigation
  const { folderTree, mutate: mutateFolders, isLoading: folderTreeLoading } = useFolderTree(
    slug as string,
    { analysisSetOnly: deepAnalysisOnly }
  );

  const isLoading = documentsLoading || foldersLoading;

  // Auto-refresh when a relevant batch completes
  const handleBatchRefresh = useCallback(() => {
    mutate();
    mutateFolders();
    mutateFoldersList();
  }, [mutate, mutateFolders, mutateFoldersList]);

  useBatchCompletionRefresh(
    slug as string,
    currentFolder?.folder_id,
    folders,
    handleBatchRefresh
  );

  // Handle file upload completion
  const handleUploadComplete = () => {
    setTargetFolderId(null); // Reset target folder
    mutate(); // Refresh documents data
    mutateFolders(); // Refresh folder tree
  };

  // Handle folder operations
  const handleFolderRefresh = useCallback(async () => {
    try {
      // Refresh all folder-related data
      await mutateFolders(); // Refresh folder tree
      await mutateFoldersList(); // Refresh flat folder list
      await mutate(); // Refresh documents
    } catch (error) {
      console.error('Error refreshing folder data:', error);
    }
  }, [mutateFolders, mutateFoldersList, mutate]);

  // Handle document deletion
  const handleDocumentDelete = useCallback(async (documentIds: string[], options?: DocumentDeleteOptions) => {
    if (!documents) return;

    try {
      const queryString = buildDeleteQueryString(options);
      await backendClient.post(`/correspondence/${slug}/delete-files${queryString}`, documentIds);

      // Wait longer to ensure backend processing is complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Refresh all relevant data sources to ensure UI updates
      // Use Promise.allSettled to ensure all refreshes are attempted even if one fails
      const refreshResults = await Promise.allSettled([
        mutate(), // Refresh documents in current folder
        mutateFolders(), // Refresh folder tree (for document counts)
        mutateFoldersList() // Refresh flat folder list
      ]);

      // Log any refresh failures for debugging
      refreshResults.forEach((result, index) => {
        if (result.status === 'rejected') {
          const sources = ['documents', 'folder tree', 'folder list'];
          console.warn(`Failed to refresh ${sources[index]}:`, result.reason);
        }
      });

      // Clear selection and show success message
      setSelectedRows([]);
      toast.success(`Successfully deleted ${documentIds.length} document${documentIds.length > 1 ? 's' : ''}`);
    } catch (error: any) {
      // Don't toast for 409 (bundle reference warning) — the UI dialog handles it
      if (error?.response?.status !== 409) {
        console.error('Error deleting documents:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to delete documents';
        toast.error(errorMessage);
      }
      throw error;
    }
  }, [slug, mutate, mutateFolders, mutateFoldersList, documents]);

  // Handle drag and drop file uploads - open dialog instead of direct upload
  const handleDropFiles = useCallback(async (files: File[]) => {
    if (!currentFolder) return;

    // Filter files using shared utility with toast notifications
    const validFiles = filterFilesWithToast(files);
    if (!validFiles) return;

    // Open the upload dialog with the filtered files
    fileUploadRef.current?.openWithFiles(validFiles);
  }, [currentFolder]);

  // Create file upload ref
  const fileUploadRef = useRef<FileUploadHandles>(null);

  // Handle empty state click
  const handleEmptyStateClick = () => {
    // Trigger the file upload dialog when empty state is clicked
    fileUploadRef.current?.openDialog();
  };

  // Handle files dropped directly onto an inline subfolder row: route the upload to that folder.
  const handleFilesDroppedOnFolder = useCallback((files: File[], folderId: number) => {
    const validFiles = filterFilesWithToast(files);
    if (!validFiles) return;

    const folderIdString = folderId.toString();
    setTargetFolderId(folderIdString);
    fileUploadRef.current?.openWithFiles(validFiles, folderIdString);
  }, []);

  // Check if current folder is a correspondence subfolder (but not the root correspondence folder or the attachments folder)
  const isCorrespondenceSubfolder = useMemo(() => {
    if (!currentFolder) return false;
    const folderPath = currentFolder.path?.toLowerCase() ?? '';
    return isInCorrespondenceFolderTree(currentFolder) &&
           !isCorrespondenceFolder(currentFolder) &&
           !folderPath.startsWith(CORRESPONDENCE_ATTACHMENTS_FOLDER_PATH);
  }, [currentFolder]);
  const { deadlines } = useDeadlines(isCorrespondenceSubfolder ? slug as string : undefined);
  const deadlineTaskStates = useMemo(
    () => Object.fromEntries(deadlines.map(deadline => [deadline.id, deadline.state])),
    [deadlines]
  );

  // Handle auto-opening a document if specified in query params
  useEffect(() => {
    const openDocumentGuid = search.open;
    if (openDocumentGuid && slug && !documentsLoading) {
      const documentExists = documents?.some(doc => doc.guid === openDocumentGuid);
      if (documentExists) {
        const documentUrl = `/matter/${slug}/f/${openDocumentGuid}?from=case-files/folder/${folderGuid}`;
        window.open(documentUrl, '_blank');

        navigate({
          to: '/matter/$slug/case-files/folder/$folderGuid',
          params: { slug, folderGuid },
          search: (prev) => ({ ...prev, open: undefined }),
          replace: true,
        });
      }
    }
  }, [navigate, slug, documents, documentsLoading, folderGuid, search.open]);

  // Create columns for the DataTable - use ColumnsCorrespondence for correspondence subfolders
  const columns = useMemo(() => {
    const foldersByGuid = buildFolderTreeMap(folderTree);
    const baseColumns = isCorrespondenceSubfolder
      ? ColumnsCorrespondence({
        refreshData: () => mutate(),
        handleDelete: handleDocumentDelete,
        slug: slug as string | undefined,
        openDocumentPanel,
        deadlineTaskStates,
        deepAnalysisEnabled,
      })
      : ColumnsCaseFiles({
        refreshData: () => mutate(),
        documentTypeSlug: currentFolder?.name || 'folder',
        handleDelete: handleDocumentDelete,
        folderGuid: currentFolder?.guid,
        slug: slug as string | undefined,
        deepAnalysisEnabled,
        openDocumentPanel,
        fileNameLabel: 'Name',
      });
    return enhanceColumnsForFolderRows(baseColumns, {
      matterSlug: slug as string,
      onRefresh: handleFolderRefresh,
      foldersByGuid,
      emptyColumnIds: isCorrespondenceSubfolder
        ? ['tags', 'from', 'to', 'attachments', 'date']
        : ['tags', 'date'],
      showFolderNameInSummary: isCorrespondenceSubfolder,
      deepAnalysisEnabled,
      deepAnalysisOnly,
    });
  }, [mutate, currentFolder?.name, currentFolder?.guid, handleDocumentDelete, isCorrespondenceSubfolder, slug, openDocumentPanel, deadlineTaskStates, deepAnalysisEnabled, deepAnalysisOnly, folderTree, handleFolderRefresh]);

  // Folder-row badge counts (direct subfolder count + recursive file count) sourced from the
  // folder tree, so deeper folders don't need their documents fetched just to show a count.
  const folderContentCounts = useMemo(
    () => buildFolderContentCounts(folderTree),
    [folderTree],
  );

  // Build the combined folder + document dataset for inline folder rows. In Deep Analysis mode the
  // flat folder list is unfiltered, so restrict folder rows to the analysis-set folders in the tree.
  const tableData = useMemo<FolderTableRow<Document>[]>(() => {
    // Only restrict to the analysis set once the tree has loaded. While it's still fetching (its
    // SWR key switches on the DA toggle and briefly returns []), an empty allow-list would filter
    // out every folder row.
    const allowedFolderIds = deepAnalysisOnly && !folderTreeLoading
      ? buildFolderTreeIdSet(folderTree)
      : undefined;
    return buildFolderTableData<Document>(
      folders,
      currentFolder?.folder_id,
      documents || [],
      folderContentCounts,
      allowedFolderIds,
    );
  }, [folders, currentFolder?.folder_id, documents, folderContentCounts, deepAnalysisOnly, folderTreeLoading, folderTree]);

  // Shared handlers for folder-row navigation and class names.
  const folderTableHandlers = useMemo(() => getFolderTableHandlers<Document>({
    slug: slug as string,
    navigate,
    openDocumentPanel,
    canExpandDocumentRow: canRowExpand,
    deepAnalysisOnly,
  }), [slug, navigate, openDocumentPanel, deepAnalysisOnly]);

  // Refresh data function for the isolated data table
  const refreshData = useCallback(() => {
    mutate();
  }, [mutate]);

  // Attachment filter options — attachments are objects rather than scalar facet
  // values, so derive their names from the data (only meaningful for
  // correspondence subfolders; harmless for other folders).
  const attachmentOptions = useMemo<FilterFieldOption[]>(() => {
    const names = new Set<string>();
    (documents ?? []).forEach(doc => {
      doc.attachments?.forEach(attachment => {
        if (attachment.fileName) names.add(attachment.fileName);
      });
    });
    return Array.from(names).sort().map(name => ({ label: name, value: name }));
  }, [documents]);

  // Filters menu config: column-driven fields (from each column's `meta.filter`)
  // plus the Deep Analysis view scope folded in as a single choice field.
  const filterFields = useMemo<FilterField[]>(() => {
    const fields: FilterField[] = deriveFilterFields(columns, {
      summary: { placeholder: 'Search summary, filename...' },
      attachments: { options: attachmentOptions },
    });
    if (deepAnalysisEnabled) {
      fields.push({
        id: 'analysisView',
        label: 'View',
        type: 'choice',
        options: [
          { label: 'All files', value: 'all' },
          { label: `${DEEP_ANALYSIS_LABEL} only`, value: 'analysis' },
        ],
        value: deepAnalysisOnly ? 'analysis' : 'all',
        defaultValue: 'all',
        onChange: (value) => setDeepAnalysisOnly(value === 'analysis'),
      });
    }
    return fields;
  }, [columns, attachmentOptions, deepAnalysisEnabled, deepAnalysisOnly, setDeepAnalysisOnly]);

  // Render expanded attachment rows as actual <tr> elements for column alignment.
  // Folder rows are not documents, so skip attachment rendering for them.
  const renderSubRows = useCallback((row: Row<FolderTableRow<Document>>) => {
    if (row.original.isFolderRow) return null;
    return (
      <ExpandedAttachmentRows
        row={row as Row<Document>}
        slug={slug as string}
        onDocumentClick={(guid) => openDocumentPanel({ documentGuid: guid, activeTab: 'document' })}
        tableType={isCorrespondenceSubfolder ? "correspondence" : "case-files"}
      />
    );
  }, [slug, openDocumentPanel, isCorrespondenceSubfolder]);

  // Build breadcrumb path
  const buildBreadcrumbPath = (folder: any, allFolders: any[]): any[] => {
    if (!folder) return [];

    const path = [folder];
    let current = folder;

    while (current.parent_folder_id) {
      const parent = allFolders.find(f => f.folder_id === current.parent_folder_id);
      if (parent) {
        path.unshift(parent);
        current = parent;
      } else {
        break;
      }
    }

    return path;
  };

  const breadcrumbPath = useMemo(
    () => (currentFolder ? buildBreadcrumbPath(currentFolder, folders) : []),
    [currentFolder, folders]
  );
  const isClientDocumentsFolder = useMemo(
    () => (currentFolder ? isClientDocumentsRoot(currentFolder) : false),
    [currentFolder]
  );
  const breadcrumbSubheader = useMemo(() => (
    <div className="block min-w-0 -mt-1">
      <nav className="min-w-0 text-gray-500" aria-label="Folder breadcrumb">
        <ScrollableFolderPath>
          <Link
            to="/matter/$slug/case-files"
            params={{ slug }}
            search={deepAnalysisOnly ? { view: 'deep-analysis' } : {}}
            className="shrink-0 hover:text-gray-700 font-medium"
          >
            Case Files
          </Link>
          {breadcrumbPath.map((folder) => (
            <span key={folder.guid} className="flex shrink-0 items-center">
              <ChevronRight className="h-4 w-4 shrink-0 mx-1" aria-hidden="true" />
              {isCorrespondenceFolder(folder) ? (
                <Link
                  to="/matter/$slug/case-files/correspondence"
                  params={{ slug }}
                  search={deepAnalysisOnly ? { view: 'deep-analysis' } : {}}
                  className="hover:text-gray-700"
                  title={folder.name}
                >
                  {folder.name}
                </Link>
              ) : (
                <Link
                  to="/matter/$slug/case-files/folder/$folderGuid"
                  params={{ slug, folderGuid: folder.guid }}
                  search={deepAnalysisOnly ? { view: 'deep-analysis' } : {}}
                  className="hover:text-gray-700"
                  title={folder.name}
                >
                  {folder.name}
                </Link>
              )}
            </span>
          ))}
        </ScrollableFolderPath>
      </nav>
      {isClientDocumentsFolder && (
        <p className="mt-1 text-gray-600">
          Upload early client documents here to automatically generate an{' '}
          <Link
            to="/matter/$slug/overview"
            params={{ slug }}
            search={{ stage: 'early' }}
            className="font-medium text-gray-700 underline hover:text-gray-900"
          >
            Early Case Assessment
          </Link>
        </p>
      )}
    </div>
  ), [slug, breadcrumbPath, isClientDocumentsFolder, deepAnalysisOnly]);
  useShellHeader({
    header: currentFolder?.name || 'Folder',
    subheader: breadcrumbSubheader,
  });

  const showFolderLoader = typeof selectedCase !== 'undefined' && (foldersLoading || foldersValidating) && !currentFolder;
  const showContentsLoader = typeof selectedCase !== 'undefined' && Boolean(currentFolder) && !error && isLoading;

  return (
    <div className={cn('overflow-x-hidden', (showFolderLoader || showContentsLoader) && 'flex h-full min-h-0 flex-col')}>
      {/* Render the TagEditorDialog at page level for correspondence subfolders */}
      <TagEditorDialog />

      {typeof selectedCase === 'undefined' ? (
        <NoCaseSelected />
      ) : showFolderLoader ? (
        // Show loading state while folder information is being fetched. A cached list can
        // predate a folder that was just created, so wait for its refresh too.
        <PageLoading text="Loading folder..." />
      ) : foldersError && !currentFolder ? (
        <div className="px-6 py-6">
          <div className="p-4 border border-red-200 rounded-md bg-red-50 text-red-600">
            Error loading folder data. Please try again later.
          </div>
        </div>
      ) : !currentFolder ? (
        <div className="px-6 py-6">
          <div className="p-4 border border-gray-200 rounded-md bg-gray-50 text-gray-600">
            This folder could not be found. It may have been moved or deleted.
          </div>
        </div>
      ) : (
        <div className={cn('px-6 py-6', !error && isLoading && 'flex min-h-0 flex-1 flex-col')}>
          {error ? (
            <div className="p-4 border border-red-200 rounded-md bg-red-50 text-red-600">
              Error loading folder data. Please try again later.
            </div>
          ) : isLoading ? (
            <PageLoading text="Loading folder contents..." />
          ) : (
            <>
              {/* Documents */}
              <div>
                <div className="pt-2 sm:pt-4 lg:pt-6">
                <IsolatedDataTable
                  columns={columns}
                  data={tableData}
                  refreshData={refreshData}
                  onRowsSelected={(rows) => setSelectedRows(rows)}
                  onDelete={handleDocumentDelete}
                  getDeleteAttachmentCount={(rows) =>
                    getDeleteAttachmentCount(
                      rows.filter((row) => !row.isFolderRow) as Document[]
                    )
                  }
                  isDeleting={false}
                  isLoading={isLoading || documentsLoading}
                  slug={slug as string}
                  documentType={currentFolder?.name || 'Documents'}
                  documentTypeSlug={currentFolder?.name?.toLowerCase() || 'documents'}
                  folderGuid={currentFolder?.guid}
                  enableBulkTags={isCorrespondenceSubfolder}
                  onDropFiles={handleDropFiles}
                  onFilesDroppedOnFolder={handleFilesDroppedOnFolder}
                  isProcessingDrop={false}
                  onEmptyStateClick={handleEmptyStateClick}
                  filterFields={filterFields}
                  syncButton={
                    <div className="flex flex-col items-end gap-2">
                      <div className="flex gap-2 items-center">
                        <ConnectSourcesControls matterId={slug as string} />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCreateFolderDialogOpen(true)}
                          icon={FolderPlus}
                          text="Create Subfolder"
                          textClassName={toolbarActionLabelClass}
                          tooltip="Create Subfolder"
                          aria-label="Create Subfolder"
                        />
                        <FileUpload
                          ref={fileUploadRef}
                          matterGuid={slug as string}
                          buttonText="Upload Files"
                          onUploadComplete={handleUploadComplete}
                          onProcessingStateChange={setIsUploading}
                          showAsDialog={true}
                          documentType={targetFolderId || currentFolder?.folder_id?.toString()}
                        />
                      </div>
                    </div>
                  }
                  onAnyRowClick={folderTableHandlers.onAnyRowClick}
                  getRowClassName={folderTableHandlers.getRowClassName}
                  renderSubRows={renderSubRows}
                  getRowCanExpand={folderTableHandlers.getRowCanExpand}
                  onRowActivate={folderTableHandlers.onRowActivate}
                  onRowDoubleClick={folderTableHandlers.onRowDoubleClick}
                  deepAnalysisEnabled={deepAnalysisEnabled}
                />
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <CreateFolderDialog
        matterSlug={slug as string}
        parentFolder={currentFolder}
        isOpen={createFolderDialogOpen}
        onClose={() => setCreateFolderDialogOpen(false)}
        onSuccess={handleFolderRefresh}
      />
    </div>
  );
};
export default FolderView;
